import * as THREE from 'three';
import { RoundedBoxGeometry } from 'three/addons/geometries/RoundedBoxGeometry.js';

import type { ElectrolysisSceneState } from './electrochemicalExplore';
import {
  type LabSceneFactory,
  createBeaker,
  createLabStage,
  createRippleSurface,
  refractiveLiquidMaterial,
  seeded,
} from './labScene3d';

export type { ElectrolysisSceneState } from './electrochemicalExplore';

/**
 * Electrolysis cell for the Explore view: a beaker of electrolyte (or a crucible of melt), two
 * plate electrodes wired to a DC power pack, a metal deposit on the cathode whose thickness follows
 * the deposited mass, and gas bubbles whose rate follows the current. Anode (+) is on the left and
 * cathode (−) on the right, as in the 2D schematic. A teaching view, not to scale.
 */

const BENCH_Y = -1.82;
const FLOOR_Y = BENCH_Y + 0.005;
const BEAKER_H = 3.2;
const AQUEOUS_DEPTH = 1.9;
const CRUCIBLE = { radius: 1.35, height: 2.3, wall: 0.12 } as const;
const MELT_DEPTH = 1.55;
const ELECTRODE = { x: 0.62, width: 0.07, depth: 0.55, bottomY: FLOOR_Y + 0.35, topY: 2.0 } as const;
const DEPOSIT = { min: 0.012, max: 0.09 } as const;
const BUBBLES_PER_ELECTRODE = 40;
// Beside the beaker, not behind it: seen through the refractive electrolyte a pack behind looked submerged.
const PACK = { x: 3.5, z: -0.4, width: 2.2, height: 0.86, depth: 1.15 } as const;

/** Named states for screenshot review of every visible change. */
export const ELECTROLYSIS_PREVIEW_STATES: Record<string, ElectrolysisSceneState> = {
  'copper-sulfate-start': { electrolyte: 'blue', electrodes: 'inert', cathode: 'copper', anode: 'gas', amount: 0.04, currentShare: 0.2 },
  'copper-sulfate-plated': { electrolyte: 'blue', electrodes: 'inert', cathode: 'copper', anode: 'gas', amount: 0.85, currentShare: 0.9 },
  'copper-refining': { electrolyte: 'blue', electrodes: 'copper', cathode: 'copper', anode: 'dissolves', amount: 0.7, currentShare: 0.5 },
  'silver-nitrate': { electrolyte: 'colourless', electrodes: 'inert', cathode: 'silver', anode: 'gas', amount: 0.5, currentShare: 0.5 },
  'sulfuric-acid-gases': { electrolyte: 'colourless', electrodes: 'inert', cathode: 'gas', anode: 'gas', amount: 0.5, currentShare: 1 },
  'molten-lead-bromide': { electrolyte: 'molten', electrodes: 'inert', cathode: 'metal-pool-bottom', anode: 'gas', amount: 0.6, currentShare: 0.6 },
};

export const createElectrolysisScene: LabSceneFactory<ElectrolysisSceneState> = (renderer, palette, reducedMotion) => {
  const stage = createLabStage(renderer, palette, reducedMotion, { benchY: BENCH_Y, cameraPosition: [1.6, 3.0, 13.4], target: [1.2, 0.2, -0.2] });
  const { scene, keep } = stage;

  // Glass beaker for aqueous electrolytes.
  const beaker = createBeaker(stage, { floorY: FLOOR_Y, height: BEAKER_H });
  const innerR = beaker.innerRadius;
  const aqueousTop = beaker.innerFloorY + AQUEOUS_DEPTH;

  const blueMaterial = refractiveLiquidMaterial(stage, palette.liquid, { attenuationDistance: 1.6 });
  const clearMaterial = refractiveLiquidMaterial(stage, palette.liquid.clone().lerp(palette.glass, 0.85), { tint: 0.7, attenuationDistance: 12 });
  const liquidBody = new THREE.Mesh(keep(new THREE.CylinderGeometry(innerR, innerR, AQUEOUS_DEPTH, 96, 1, true)), blueMaterial);
  liquidBody.position.y = beaker.innerFloorY + AQUEOUS_DEPTH / 2;
  const liquidFloor = new THREE.Mesh(keep(new THREE.CircleGeometry(innerR, 96)), blueMaterial);
  liquidFloor.rotation.x = -Math.PI / 2;
  liquidFloor.position.y = beaker.innerFloorY + 0.002;
  scene.add(liquidBody, liquidFloor);
  const surface = createRippleSurface(stage, innerR, blueMaterial);
  surface.mesh.position.y = aqueousTop;

  // Opaque ceramic crucible with a glowing melt for molten electrolytes.
  const crucible = new THREE.Group();
  const ceramic = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.heat, 0.12).lerp(palette.metal, 0.12), roughness: 0.75 }));
  const { radius: CR, height: CH, wall: CW } = CRUCIBLE;
  const cruciblePoints = [
    [0, 0], [CR - 0.15, 0], [CR, 0.15], [CR + 0.08, CH], [CR + 0.08 - CW, CH], [CR - CW, 0.15 + CW], [CR - 0.15, CW], [0, CW],
  ].map(([x, y]) => new THREE.Vector2(x, y));
  const crucibleBody = new THREE.Mesh(keep(new THREE.LatheGeometry(cruciblePoints, 96)), ceramic);
  crucibleBody.castShadow = true;
  crucibleBody.receiveShadow = true;
  const meltTop = FLOOR_Y + CW + MELT_DEPTH;
  const meltRadius = CR - CW + (0.08 * MELT_DEPTH) / CH;
  const meltColour = palette.glow.clone().lerp(palette.heat, 0.4);
  const melt = new THREE.Mesh(
    keep(new THREE.CircleGeometry(meltRadius, 96)),
    keep(new THREE.MeshPhysicalMaterial({ color: meltColour, emissive: meltColour, emissiveIntensity: 0.75, roughness: 0.25, clearcoat: 0.6 })),
  );
  melt.rotation.x = -Math.PI / 2;
  melt.position.y = meltTop;
  const meltLight = new THREE.PointLight(meltColour, 6, 5, 1.6);
  meltLight.position.set(0, meltTop + 0.6, 0);
  crucibleBody.position.y = FLOOR_Y;
  crucible.add(crucibleBody, melt, meltLight);
  scene.add(crucible);

  // Electrodes: graphite (inert) or copper plates; the submerged part can thin as a copper anode dissolves.
  const graphite = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.bench, 0.3), roughness: 0.65, metalness: 0.2 }));
  const copperColour = palette.heat.clone().lerp(palette.glow, 0.3);
  const copper = keep(new THREE.MeshPhysicalMaterial({ color: copperColour, metalness: 0.9, roughness: 0.32, clearcoat: 0.4 }));
  const silver = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.22), metalness: 1, roughness: 0.28 }));
  const liquidMetal = keep(new THREE.MeshPhysicalMaterial({ color: palette.bench.clone().lerp(palette.metal, 0.35), metalness: 1, roughness: 0.12, clearcoat: 1 }));
  const plateGeometry = keep(new THREE.BoxGeometry(ELECTRODE.width, 1, ELECTRODE.depth));
  const makeElectrode = (x: number) => {
    const upper = new THREE.Mesh(plateGeometry, graphite);
    const lower = new THREE.Mesh(plateGeometry, graphite);
    upper.castShadow = true;
    lower.castShadow = true;
    upper.position.x = x;
    lower.position.x = x;
    scene.add(upper, lower);
    return { upper, lower };
  };
  const anode = makeElectrode(-ELECTRODE.x);
  const cathode = makeElectrode(ELECTRODE.x);
  const deposit = new THREE.Mesh(plateGeometry, copper);
  deposit.position.x = ELECTRODE.x;
  scene.add(deposit);

  // Molten metal product: a pool on the crucible floor (denser metals) or a layer floating by the cathode.
  const poolGeometry = keep(new THREE.CylinderGeometry(1, 1, 1, 64));
  const pool = new THREE.Mesh(poolGeometry, liquidMetal);
  scene.add(pool);

  // Power pack with + (red) and − (black) terminals and a current lamp whose glow follows the current.
  const packMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal.clone().lerp(palette.liquid, 0.25), roughness: 0.4, clearcoat: 0.7 }));
  const pack = new THREE.Mesh(keep(new RoundedBoxGeometry(PACK.width, PACK.height, PACK.depth, 4, 0.08)), packMaterial);
  pack.position.set(PACK.x, BENCH_Y + PACK.height / 2, PACK.z);
  pack.castShadow = true;
  pack.receiveShadow = true;
  const packTop = BENCH_Y + PACK.height;
  const terminalGeometry = keep(new THREE.CylinderGeometry(0.11, 0.13, 0.22, 24));
  const positiveMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.heat, roughness: 0.3, clearcoat: 1 }));
  const negativeMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.metal, roughness: 0.3, clearcoat: 1 }));
  const terminalZ = PACK.z + PACK.depth / 2 - 0.3;
  const positive = new THREE.Mesh(terminalGeometry, positiveMaterial);
  positive.position.set(PACK.x - 0.55, packTop + 0.11, terminalZ);
  const negative = new THREE.Mesh(terminalGeometry, negativeMaterial);
  negative.position.set(PACK.x + 0.55, packTop + 0.11, terminalZ);
  const lampMaterial = keep(new THREE.MeshStandardMaterial({ color: palette.glow, emissive: palette.glow, emissiveIntensity: 0 }));
  const lamp = new THREE.Mesh(keep(new THREE.BoxGeometry(0.9, 0.3, 0.02)), lampMaterial);
  lamp.position.set(PACK.x, BENCH_Y + PACK.height * 0.55, PACK.z + PACK.depth / 2 + 0.011);
  scene.add(pack, positive, negative, lamp);

  const wire = (fromX: number, toX: number, material: THREE.Material) => {
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(fromX, ELECTRODE.topY - 0.05, 0),
      new THREE.Vector3(fromX * 1.1, ELECTRODE.topY + 0.45, -0.5),
      new THREE.Vector3(toX, packTop + 0.85, terminalZ + 0.7),
      new THREE.Vector3(toX, packTop + 0.2, terminalZ),
    ]);
    const mesh = new THREE.Mesh(keep(new THREE.TubeGeometry(curve, 48, 0.03, 10, false)), material);
    mesh.castShadow = true;
    scene.add(mesh);
  };
  wire(-ELECTRODE.x, positive.position.x, positiveMaterial);
  wire(ELECTRODE.x, negative.position.x, negativeMaterial);

  // Gas bubbles: transparent (not refractive), rising beside the gas electrode.
  const bubbleMaterial = keep(new THREE.MeshPhysicalMaterial({ color: palette.steam, roughness: 0.05, transparent: true, opacity: 0.7, clearcoat: 1, envMapIntensity: 2.5 }));
  const bubbles = new THREE.InstancedMesh(keep(new THREE.SphereGeometry(0.045, 12, 8)), bubbleMaterial, BUBBLES_PER_ELECTRODE * 2);
  scene.add(bubbles);

  stage.finish();

  const matrix = new THREE.Matrix4();
  const position = new THREE.Vector3();
  const quaternion = new THREE.Quaternion();
  const scale = new THREE.Vector3();

  const placePlate = (mesh: THREE.Mesh, bottom: number, top: number, thin = 1) => {
    const height = Math.max(top - bottom, 0.001);
    mesh.scale.set(thin, height, thin);
    mesh.position.y = bottom + height / 2;
  };

  return {
    update: (state, t, playing) => {
      const molten = state.electrolyte === 'molten';
      const amount = THREE.MathUtils.clamp(state.amount, 0, 1);
      const currentShare = THREE.MathUtils.clamp(state.currentShare, 0, 1);
      const liquidTop = molten ? meltTop : aqueousTop;

      beaker.group.visible = !molten;
      liquidBody.visible = !molten;
      liquidFloor.visible = !molten;
      surface.mesh.visible = !molten;
      crucible.visible = molten;
      const liquid = state.electrolyte === 'blue' ? blueMaterial : clearMaterial;
      liquidBody.material = liquid;
      liquidFloor.material = liquid;
      surface.mesh.material = liquid;
      if (!molten) surface.update(0.004 + currentShare * 0.01, t, false);

      // Electrodes: copper plates for electrorefining, graphite otherwise.
      const plate = state.electrodes === 'copper' ? copper : graphite;
      [anode.upper, anode.lower, cathode.upper, cathode.lower].forEach((mesh) => {
        mesh.material = plate;
      });
      placePlate(anode.upper, liquidTop, ELECTRODE.topY);
      placePlate(cathode.upper, liquidTop, ELECTRODE.topY);
      placePlate(anode.lower, ELECTRODE.bottomY, liquidTop, state.anode === 'dissolves' ? 1 - 0.55 * amount : 1);
      placePlate(cathode.lower, ELECTRODE.bottomY, liquidTop);

      // Metal deposit on the cathode: thickness follows the deposited mass.
      const solidDeposit = state.cathode === 'copper' || state.cathode === 'silver';
      deposit.visible = solidDeposit && amount > 0;
      if (deposit.visible) {
        const thickness = DEPOSIT.min + amount * (DEPOSIT.max - DEPOSIT.min);
        deposit.material = state.cathode === 'silver' ? silver : copper;
        const height = liquidTop - ELECTRODE.bottomY - 0.02;
        deposit.scale.set((ELECTRODE.width + 2 * thickness) / ELECTRODE.width, height, (ELECTRODE.depth + 2 * thickness) / ELECTRODE.depth);
        deposit.position.y = ELECTRODE.bottomY + height / 2;
      }

      // Molten metal pool or floating layer.
      const poolThickness = 0.03 + amount * 0.22;
      pool.visible = molten && (state.cathode === 'metal-pool-bottom' || state.cathode === 'metal-pool-top') && amount > 0;
      if (state.cathode === 'metal-pool-bottom') {
        pool.scale.set(meltRadius - 0.12, poolThickness, meltRadius - 0.12);
        pool.position.set(0, FLOOR_Y + CW + poolThickness / 2, 0);
      } else {
        pool.scale.set(0.42, poolThickness * 0.5, 0.42);
        pool.position.set(ELECTRODE.x, meltTop + (poolThickness * 0.5) / 2 - 0.005, 0);
      }

      lampMaterial.emissiveIntensity = 0.3 + currentShare * 2.2;

      // Bubbles: anode gas on the left, cathode gas on the right; rate follows the current.
      const active = Math.round(currentShare * BUBBLES_PER_ELECTRODE);
      const sides: Array<[boolean, number]> = [
        [state.anode === 'gas', -ELECTRODE.x],
        [state.cathode === 'gas', ELECTRODE.x],
      ];
      sides.forEach(([on, x], side) => {
        for (let index = 0; index < BUBBLES_PER_ELECTRODE; index += 1) {
          const slot = side * BUBBLES_PER_ELECTRODE + index;
          if (!on || index >= active) {
            bubbles.setMatrixAt(slot, matrix.makeScale(0, 0, 0));
            continue;
          }
          const progress = (t * (0.35 + seeded(slot, 3) * 0.4) + seeded(slot, 4)) % 1;
          const startY = molten ? liquidTop - 0.12 : ELECTRODE.bottomY + seeded(slot, 5) * (liquidTop - ELECTRODE.bottomY) * 0.6;
          const sideOffset = (seeded(slot, 6) > 0.5 ? 1 : -1) * (ELECTRODE.width / 2 + 0.06 + seeded(slot, 7) * 0.05);
          position.set(
            x + sideOffset + Math.sin(t * 5 + slot) * 0.01,
            startY + progress * (liquidTop - startY + (molten ? 0.14 : 0)),
            (seeded(slot, 8) - 0.5) * ELECTRODE.depth,
          );
          bubbles.setMatrixAt(slot, matrix.compose(position, quaternion.identity(), scale.setScalar(0.6 + seeded(slot, 9) * 0.8)));
        }
      });
      bubbles.instanceMatrix.needsUpdate = true;

      stage.render(playing);
    },
    resize: stage.resize,
    dispose: stage.dispose,
  };
};
